"use client";
import { motion } from "framer-motion";

export default function Hero() {
  const columns = [
    { number: "#01", title: "Full stack developer" },
    { number: "#02", title: "Android Developer" },
    { number: "#03", title: "System Architect" },
    { number: "#04", title: "Web Security" },
  ];

  return (
    <section className="w-full h-screen bg-black p-3 sm:p-4 md:p-6 relative flex flex-col justify-between overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="relative flex-grow w-full h-full rounded-t-[1.5rem] sm:rounded-t-[2.5rem] md:rounded-t-[3.5rem] lg:rounded-t-[4rem] rounded-b-[3rem] sm:rounded-b-[4.5rem] md:rounded-b-[6rem] lg:rounded-b-[7rem] overflow-hidden flex flex-col justify-between p-8 sm:p-12 lg:p-16 xl:p-20 bg-black"
      >
        {/* Backdrop and profile graphic container */}
        <div className="absolute inset-0 z-0 overflow-hidden flex items-end justify-center">
          {/* Radial gradient backing to highlight the graphic with a deep red-orange glow */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,_#ff5500_0%,_#c31a00_30%,_#3d0000_65%,_#000000_100%)] pointer-events-none z-0 opacity-45" />

          <img
            src="/bg.png"
            alt="Adhwaith A S"
            className="h-[85%] md:h-[95%] w-auto max-w-none object-contain object-bottom pointer-events-none select-none z-10"
          />

          {/* Color-grading tint overlay to visually bind the graphic with the background glow */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#ff4500]/5 via-[#ff3b00]/15 to-transparent mix-blend-color pointer-events-none z-20" />
        </div>

        {/* Dynamic vignette boundaries to smoothly fade image borders to pitch black */}
        <div className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black via-black/85 to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-black to-transparent z-10 pointer-events-none" />

        {/* Ambient border outlines and bottom highlight line to establish depth */}
        <div className="absolute inset-0 rounded-[inherit] border border-white/5 pointer-events-none z-30" />
        
        {/* Glowing border highlights specifically on both bottom corners */}
        <div 
          className="absolute inset-0 rounded-[inherit] border border-[#ff5500]/30 pointer-events-none z-30" 
          style={{
            WebkitMaskImage: 'radial-gradient(circle at bottom left, black 0%, transparent 40%), radial-gradient(circle at bottom right, black 0%, transparent 40%)',
            maskImage: 'radial-gradient(circle at bottom left, black 0%, transparent 40%), radial-gradient(circle at bottom right, black 0%, transparent 40%)'
          }}
        />

        {/* Upward reflection lighting to emulate glass reflection glow */}
        <div className="absolute bottom-0 inset-x-0 h-40 bg-[radial-gradient(ellipse_at_bottom,_rgba(255,85,0,0.18)_0%,_rgba(255,85,0,0.05)_45%,_transparent_75%)] blur-xl z-10 pointer-events-none" />

        <div className="relative z-20 flex flex-col justify-between h-full w-full flex-grow pt-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end w-full mt-auto mb-10 sm:mb-14 md:mb-16">
            <div className="lg:col-span-7">
              <motion.span
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-base sm:text-lg md:text-xl font-normal text-white/90 mb-2 sm:mb-3 block tracking-wide font-sans"
              >
                Hey, I'm
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                className="text-5xl sm:text-7xl md:text-8xl lg:text-[7.2rem] xl:text-[8rem] font-bold leading-[0.9] tracking-tight text-white font-sans"
              >
                Adhwaith
                <br />A S
              </motion.h1>
            </div>

            <div className="lg:col-span-5 lg:pl-6 xl:pl-12">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.7 }}
                className="text-xl sm:text-2xl md:text-3xl font-medium text-white mb-3 sm:mb-4 leading-snug font-sans"
              >
                Clean code should <br className="hidden sm:inline" /> feel
                invisible.
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.7 }}
                className="text-white/70 text-xs sm:text-sm md:text-base font-light leading-relaxed max-w-sm font-sans"
              >
                I build fast backends, responsive apps, and secure databases.{" "}
                <br className="hidden md:inline" /> Just solid software that
                works.
              </motion.p>
            </div>
          </div>

          <div className="w-full h-px bg-gradient-to-r from-transparent via-white/15 to-transparent mb-8" />

          {/* Staggered entry competencies grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 pb-4 sm:pb-8 lg:pb-12 w-full">
            {columns.map((col, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + idx * 0.1, duration: 0.6 }}
                className="flex flex-col"
              >
                <span className="text-[#ff5500] font-mono text-xs sm:text-sm font-bold tracking-wider">
                  {col.number}
                </span>
                <span className="text-white/95 text-xs sm:text-sm md:text-base font-medium mt-1.5 font-sans">
                  {col.title}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
